import { useState, useMemo } from 'react';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import {
  catalogApiRef,
  entityRouteParams,
  entityRouteRef,
  useEntity,
} from '@backstage/plugin-catalog-react';
import { useNavigate } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import LabelIcon from '@material-ui/icons/Label';
import ViewListIcon from '@material-ui/icons/ViewList';
import { makeStyles } from '@material-ui/core/styles';
import { Progress } from '@backstage/core-components';
import { Alert } from '@material-ui/lab';
import useAsync from 'react-use/lib/useAsync';
import { Project } from '@internal/plugin-pulse-common';

const useStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
  },
  cardContent: {
    flex: '1 1 auto',
    overflowY: 'auto',
    maxHeight: 400,
    padding: theme.spacing(0),
    '&:last-child': {
      paddingBottom: 0,
    },
  },
  accordion: {
    boxShadow: 'none',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:before': {
      display: 'none',
    },
    '&.Mui-expanded': {
      margin: 0,
    },
  },
  accordionSummary: {
    minHeight: 40,
    '&.Mui-expanded': {
      minHeight: 40,
    },
    '& .MuiAccordionSummary-content': {
      margin: `${theme.spacing(0.75)}px 0`,
      alignItems: 'center',
      gap: theme.spacing(1),
    },
    '& .MuiAccordionSummary-content.Mui-expanded': {
      margin: `${theme.spacing(0.75)}px 0`,
    },
  },
  tagLabel: {
    fontWeight: 600,
    fontSize: '0.8125rem',
    color: theme.palette.text.primary,
  },
  countBadge: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  accordionDetails: {
    padding: theme.spacing(1, 2, 1.5),
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    display: 'flex',
  },
  flatList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    padding: theme.spacing(2),
  },
  chip: {
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    fontWeight: 500,
    fontSize: '0.75rem',
    height: 24,
    cursor: 'pointer',
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    '&:hover': {
      opacity: 0.85,
    },
  },
  emptyState: {
    padding: theme.spacing(3),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
}));

type TechEntry = { name: string; ref: string };

export function TechnologiesCard() {
  const classes = useStyles();
  const [groupByTag, setGroupByTag] = useState(false);
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const navigate = useNavigate();
  const entityRoute = useRouteRef(entityRouteRef);

  const relatedTechnologies =
    (entity.spec as Project)?.relatedTechnologies ?? [];

  const refs = relatedTechnologies.map(t => t.ref);
  const refsKey = refs.join(',');

  const {
    value: grouped,
    loading,
    error,
  } = useAsync(async () => {
    if (!refs.length) return new Map<string, TechEntry[]>();

    const result = await catalogApi.getEntitiesByRefs({
      entityRefs: refs,
      fields: [
        'kind',
        'metadata.name',
        'metadata.namespace',
        'metadata.title',
        'spec.tags',
      ],
    });

    const tagMap = new Map<string, TechEntry[]>();

    (result.items ?? []).forEach((item, idx) => {
      const techEntity =
        item && typeof item === 'object' && 'entity' in item
          ? (item as any).entity
          : item;

      if (!techEntity?.metadata) return;

      const techEntry: TechEntry = {
        name:
          techEntity.metadata.title ||
          techEntity.metadata.name ||
          refs[idx] ||
          'Unknown',
        ref: refs[idx] ?? '',
      };

      const tags: string[] = Array.isArray(techEntity.spec?.tags)
        ? (techEntity.spec.tags as string[])
        : [];

      const effectiveTags = tags.length ? tags : ['Uncategorized'];

      for (const tag of effectiveTags) {
        if (!tagMap.has(tag)) tagMap.set(tag, []);
        tagMap.get(tag)!.push(techEntry);
      }
    });

    // Sort tags alphabetically, keep 'Uncategorized' last
    const sorted = new Map<string, TechEntry[]>(
      [...tagMap.entries()].sort(([a], [b]) => {
        if (a === 'Uncategorized') return 1;
        if (b === 'Uncategorized') return -1;
        return a.localeCompare(b);
      }),
    );

    return sorted;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogApi, refsKey]);

  const allTechs = useMemo(() => {
    if (!grouped) return [];
    const seen = new Set<string>();
    const list: TechEntry[] = [];
    for (const techs of grouped.values()) {
      for (const tech of techs) {
        if (!seen.has(tech.ref)) {
          seen.add(tech.ref);
          list.push(tech);
        }
      }
    }
    return list.sort((a, b) => a.name.localeCompare(b.name));
  }, [grouped]);

  const hasData = !loading && !error && grouped && grouped.size > 0;

  return (
    <Card className={classes.card}>
      <CardHeader
        title="Technologies"
        action={
          hasData ? (
            <Tooltip title={groupByTag ? 'Show as list' : 'Group by tag'}>
              <IconButton
                size="small"
                onClick={() => setGroupByTag(g => !g)}
                aria-label={groupByTag ? 'Show as list' : 'Group by tag'}
              >
                {groupByTag ? <ViewListIcon /> : <LabelIcon />}
              </IconButton>
            </Tooltip>
          ) : undefined
        }
      />
      <CardContent className={classes.cardContent}>
        {loading && <Progress />}
        {!loading && error && (
          <Alert severity="error">
            Failed to load technologies: {error.message}
          </Alert>
        )}
        {!loading && !error && (!grouped || grouped.size === 0) && (
          <Typography className={classes.emptyState}>
            No related technologies
          </Typography>
        )}
        {hasData &&
          groupByTag &&
          [...grouped!.entries()].map(([tag, techs]) => (
            <Accordion key={tag} className={classes.accordion} defaultExpanded>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                className={classes.accordionSummary}
              >
                <Typography className={classes.tagLabel}>{tag}</Typography>
                <Typography className={classes.countBadge}>
                  ({techs.length})
                </Typography>
              </AccordionSummary>
              <AccordionDetails className={classes.accordionDetails}>
                <Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                  {techs.map((tech, i) => (
                    <Chip
                      key={`${tech.name}-${i}`}
                      label={tech.name}
                      size="small"
                      className={classes.chip}
                      onClick={() =>
                        navigate(entityRoute(entityRouteParams(tech.ref)))
                      }
                    />
                  ))}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        {hasData && !groupByTag && (
          <Box className={classes.flatList}>
            {allTechs.map((tech, i) => (
              <Chip
                key={`${tech.name}-${i}`}
                label={tech.name}
                size="small"
                className={classes.chip}
                onClick={() =>
                  navigate(entityRoute(entityRouteParams(tech.ref)))
                }
              />
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
